import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GENRE_ICONS = {
  womens_beauty: "✦",
  family: "❀",
  child: "✿",
  senior: "✺",
  branding: "◆",
};

const GENRE_BLURBS = {
  womens_beauty: "Identity, visibility, confidence — beauty portraiture for women.",
  family: "Connection, belonging, legacy — multi-subject family sessions.",
  child: "Wonder, play, growth — portraiture for children.",
  senior: "Transition, aspiration, individuality, confidence — portraiture for the threshold years.",
  branding: "Authority, expertise, presence — personal & professional branding.",
};

/**
 * GenreSelector — a reusable step that gates Portrait Plan / Inspiration flows.
 *
 * Props:
 *  - value: currently selected genre key (or null)
 *  - onSelect: (genreKey) => void  (called immediately when the user picks an available genre)
 *  - title?: heading text
 *  - subtitle?: subheading text
 *
 * Since each step only accepts a single genre, clicking a tile auto-advances —
 * no separate "Continue" button is needed. We still show a brief check overlay
 * for visual feedback so the user can see what they picked.
 */
const GenreSelector = ({ value, onSelect, title = "Select a Genre", subtitle = "Choose a genre to load its options" }) => {
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  // `advancing` only tracks the just-clicked tile so we can show its spinner
  // and disable other tiles briefly. It MUST start null on every mount — if we
  // seeded it from `value` (the parent's last-picked genre), every tile other
  // than that one would render disabled and the prior tile would spinner-hang
  // when the user returns via "Change genre".
  const [advancing, setAdvancing] = useState(null);

  useEffect(() => {
    const fetchGenres = async () => {
      try {
        const resp = await axios.get(`${API}/genres`, { withCredentials: true });
        setGenres(resp.data?.genres || []);
      } catch (e) {
        const detail = e.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : detail?.message || "Could not load genres";
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };
    fetchGenres();
  }, []);

  const handlePick = (genreKey) => {
    // Guard against double-clicks while the parent is unmounting us.
    if (advancing) return;
    setAdvancing(genreKey);
    onSelect && onSelect(genreKey);
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="genre-selector">
      <div className="mb-8 text-center">
        <h2 className="font-heading text-4xl sm:text-5xl tracking-tight mb-3" data-testid="genre-selector-title">{title}</h2>
        <p className="text-muted-foreground text-sm sm:text-base">{subtitle}</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {genres.map((g) => {
            const isAvailable = g.available;
            const isAdvancing = advancing === g.key;
            const reasons = [];
            if (!g.has_pack) reasons.push("Genre Pack not loaded");
            else if (!g.has_inspo_bucket) reasons.push("Image store not configured");
            else if (!g.has_inspo_images && !g.user_image_count) reasons.push("Curated images coming soon — upload your own to unlock");

            return (
              <motion.button
                key={g.key}
                type="button"
                whileHover={isAvailable && !advancing ? { y: -2 } : {}}
                disabled={!isAvailable || (advancing && !isAdvancing)}
                onClick={() => isAvailable && handlePick(g.key)}
                data-testid={`genre-card-${g.key}`}
                className={`relative text-left p-6 rounded-sm border transition-all ${
                  isAvailable
                    ? isAdvancing
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/60 bg-card"
                    : "border-border/40 bg-card/30 opacity-50 cursor-not-allowed"
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="text-3xl text-primary leading-none">{GENRE_ICONS[g.key] || "◇"}</div>
                  {isAvailable ? (
                    isAdvancing ? (
                      <div className="text-primary" data-testid={`genre-advancing-${g.key}`}>
                        <Loader2 className="w-4 h-4 animate-spin" />
                      </div>
                    ) : null
                  ) : (
                    <Lock className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>
                <h3 className="font-heading text-xl mb-1">{g.name}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                  {GENRE_BLURBS[g.key] || ""}
                </p>
                {!isAvailable && reasons.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border/30">
                    {reasons.map((r) => (
                      <p key={r} className="text-[11px] text-muted-foreground italic">• {r}</p>
                    ))}
                  </div>
                )}
              </motion.button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GenreSelector;
